import { useRef, useEffect } from "react";

/**
 * 6-digit OTP input with auto-advance, backspace-back, paste-support.
 * Props:
 *  - value: string (up to 6 digits)
 *  - onChange: (v:string) => void
 *  - length: number (default 6)
 *  - autoFocus: bool
 *  - testIdPrefix: string
 */
export const OtpBoxes = ({ value = "", onChange, length = 6, autoFocus = true, testIdPrefix = "otp" }) => {
  const refs = useRef([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setAt = (i, ch) => {
    const arr = value.padEnd(length, " ").split("");
    arr[i] = ch;
    const next = arr.join("").replace(/\s+$/, "").slice(0, length);
    onChange(next);
  };

  const handleChange = (i, e) => {
    const ch = e.target.value.replace(/\D/g, "").slice(-1);
    if (!ch) return;
    setAt(i, ch);
    if (i < length - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      if (value[i]) {
        setAt(i, "");
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        setAt(i - 1, "");
      }
    } else if (e.key === "ArrowLeft" && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === "ArrowRight" && i < length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, length);
    if (!txt) return;
    e.preventDefault();
    onChange(txt);
    const focusIdx = Math.min(txt.length, length - 1);
    setTimeout(() => refs.current[focusIdx]?.focus(), 0);
  };

  return (
    <div className="flex gap-2 sm:gap-3 justify-center" data-testid={`${testIdPrefix}-boxes`} onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className="w-11 h-14 sm:w-12 sm:h-16 rounded-xl border-2 border-brand-navy/15 bg-white text-center font-heading font-black text-2xl sm:text-3xl text-brand-navy focus:border-brand-red focus:outline-none focus:ring-4 focus:ring-brand-red/20 transition"
          data-testid={`${testIdPrefix}-box-${i}`}
        />
      ))}
    </div>
  );
};
